// src/platform/TenantList.js — superadmin tenant management table + actions
import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../components/UI';
import {
  getTenants, createTenant, provisionTenant,
  resetTenantAdmin, suspendTenant, activateTenant, webuzoCheck,
  getTenantUsage, getTenantUsers, impersonateTenantUser,
  deleteTenant, restoreTenant, purgeTenant, downloadTenantBackup,
} from '../utils/api';

const STATUS_COLOR = {
  pending:      '#6b7280',
  provisioning: '#2563eb',
  active:       '#16a34a',
  failed:       '#dc2626',
  suspended:    '#d97706',
  deleted:      '#7c2d12',
};

function StatusBadge({ status }) {
  const color = STATUS_COLOR[status] || '#6b7280';
  return <span className="status-badge" style={{ background: color + '22', color }}>{status}</span>;
}

function fmtBytes(n) {
  if (!n) return '0 KB';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function SetupLinkModal({ url, title, note, onClose }) {
  const [copied, setCopied] = useState(false);
  if (!url) return null;
  const copy = () => { navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  return (
    <Modal open={!!url} onClose={onClose} title={title} width={540}>
      <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 12 }}>{note}</p>
      <div style={{ fontFamily: 'monospace', fontSize: 12, background: 'var(--gray-50)', padding: '10px 12px', borderRadius: 7, wordBreak: 'break-all', marginBottom: 12 }}>
        {url}
      </div>
      <div className="form-actions">
        <button className="btn btn-outline" onClick={copy}>
          <i className={`ti ${copied ? 'ti-check' : 'ti-copy'}`} /> {copied ? 'Copied!' : 'Copy link'}
        </button>
        <button className="btn btn-primary" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}

// Expanded row: usage + impersonation picker.
function TenantDetail({ tenant, toast, onImpersonateUrl }) {
  const [usage, setUsage] = useState(null);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    getTenantUsage(tenant.id).then(r => setUsage(r.data)).catch(() => setUsage({ available: false, reason: 'Could not load usage' }));
    if (tenant.status === 'active') {
      getTenantUsers(tenant.id).then(r => setUsers(r.data)).catch(() => {});
    }
  }, [tenant.id, tenant.status]);

  const impersonate = async (userId) => {
    try {
      const r = await impersonateTenantUser(tenant.id, userId);
      onImpersonateUrl(r.data.url);
    } catch (e) { toast(e.response?.data?.error || 'Could not start impersonation'); }
  };

  const cell = { fontSize: 12, color: 'var(--gray-600)' };
  return (
    <tr>
      <td colSpan={5} style={{ background: 'var(--gray-50)', padding: '14px 12px' }}>
        <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', marginBottom: 6 }}>Usage</div>
            {!usage ? <div style={cell}>Loading…</div>
              : !usage.available ? <div style={cell}>{usage.reason}</div>
              : (
                <div style={{ display: 'flex', gap: 24 }}>
                  <div><div style={cell}>Users</div><div style={{ fontWeight: 700 }}>{usage.users}</div></div>
                  <div><div style={cell}>Leads</div><div style={{ fontWeight: 700 }}>{usage.leads}</div></div>
                  <div><div style={cell}>DB size</div><div style={{ fontWeight: 700 }}>{fmtBytes(usage.db_size_bytes)}</div></div>
                  <div><div style={cell}>Last activity</div><div style={{ fontWeight: 700 }}>{usage.last_activity ? usage.last_activity.slice(0, 10) : '—'}</div></div>
                </div>
              )}
          </div>

          {tenant.status === 'active' && users.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-400)', textTransform: 'uppercase', marginBottom: 6 }}>Impersonate as</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {users.map(u => (
                  <button key={u.id} className="btn btn-outline btn-sm" onClick={() => impersonate(u.id)} title={u.email}>
                    <i className="ti ti-eye" /> {u.name}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 6 }}>Opens a 1-hour session as that user. Logged.</div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function TenantList({ toast }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newTenant, setNewTenant] = useState({ name: '', subdomain: '', admin_email: '' });
  const [saving, setSaving] = useState(false);
  const [setupUrl, setSetupUrl] = useState('');
  const [impersonateUrl, setImpersonateUrl] = useState('');
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purgeConfirm, setPurgeConfirm] = useState('');

  const load = useCallback(() => {
    getTenants().then(r => setTenants(r.data)).catch(() => toast('Failed to load tenants')).finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!newTenant.name.trim() || !newTenant.subdomain.trim()) { toast('Name and subdomain are required'); return; }
    setSaving(true);
    try {
      await createTenant(newTenant);
      setShowAdd(false);
      setNewTenant({ name: '', subdomain: '', admin_email: '' });
      toast('Tenant added — click Provision to set it up');
      load();
    } catch (e) { toast(e.response?.data?.error || 'Failed to add tenant'); }
    finally { setSaving(false); }
  };

  const act = async (id, fn, okMsg) => {
    setBusyId(id);
    try {
      const r = await fn();
      if (okMsg) toast(okMsg);
      load();
      return r;
    } catch (e) { toast(e.response?.data?.error || 'Action failed'); load(); }
    finally { setBusyId(null); }
  };

  const handleProvision = async (t) => {
    const r = await act(t.id, () => provisionTenant(t.id), 'Provisioned!');
    if (r?.data?.setup_url) setSetupUrl(r.data.setup_url);
  };

  const handleResetAdmin = async (t) => {
    const r = await act(t.id, () => resetTenantAdmin(t.id));
    if (r?.data?.setup_url) setSetupUrl(r.data.setup_url);
  };

  const handleBackup = async (t) => {
    setBusyId(t.id);
    toast('Building backup…');
    try {
      await downloadTenantBackup(t.id, t.subdomain);
      toast('Backup downloaded');
    } catch (e) { toast(e.message || 'Backup failed'); }
    finally { setBusyId(null); }
  };

  const handlePurge = async () => {
    const t = purgeTarget;
    setBusyId(t.id);
    try {
      await purgeTenant(t.id, purgeConfirm);
      toast(`${t.subdomain} purged`);
      setPurgeTarget(null); setPurgeConfirm('');
      load();
    } catch (e) { toast(e.response?.data?.error || 'Purge failed'); }
    finally { setBusyId(null); }
  };

  const handleWebuzoCheck = async () => {
    toast('Checking Webuzo connection...');
    try {
      const r = await webuzoCheck();
      toast(r.data.ok ? 'Webuzo connection OK' : `Webuzo error: ${r.data.error || 'unknown'}`);
    } catch (e) { toast(e.response?.data?.error || 'Webuzo unreachable'); }
  };

  return (
    <div>
      <div className="section-header">
        <div className="section-title">Tenants</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={handleWebuzoCheck}><i className="ti ti-plug" /> Check Webuzo</button>
          <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><i className="ti ti-plus" /> Add Tenant</button>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-400)' }}>Loading...</div>
        ) : tenants.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-400)' }}>No tenants yet — add your first one.</div>
        ) : (
          <table className="leads-table">
            <thead>
              <tr><th>Company</th><th>Default domain</th><th>Custom domain</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {tenants.map(t => (
                <React.Fragment key={t.id}>
                  <tr>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <button
                          onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: 0 }}
                          title="Usage & impersonation"
                        >
                          <i className={`ti ${expandedId === t.id ? 'ti-chevron-down' : 'ti-chevron-right'}`} />
                        </button>
                        <div>
                          <div className="lead-name">{t.name}</div>
                          {!Number(t.is_active) && <div style={{ fontSize: 11, color: 'var(--amber)' }}>suspended</div>}
                          {(t.status === 'failed' || t.provision_error) && t.provision_error && (
                            <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 2, maxWidth: 280, whiteSpace: 'normal' }}>{t.provision_error}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{t.default_domain}</td>
                    <td style={{ fontSize: 12 }}>
                      {t.custom_domain ? `${t.custom_domain} (${t.custom_domain_status})` : <span style={{ color: 'var(--gray-300)' }}>—</span>}
                    </td>
                    <td><StatusBadge status={t.status} /></td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        {(t.status === 'pending' || t.status === 'failed') && (
                          <button className="btn btn-primary btn-sm" disabled={busyId === t.id} onClick={() => handleProvision(t)}>
                            {busyId === t.id ? 'Working...' : 'Provision'}
                          </button>
                        )}
                        {t.status === 'active' && (
                          <>
                            <button className="btn btn-outline btn-sm" disabled={busyId === t.id} onClick={() => handleResetAdmin(t)} title="Re-issue the admin setup link">
                              <i className="ti ti-link" /> Setup link
                            </button>
                            <button className="btn btn-outline btn-sm" disabled={busyId === t.id} onClick={() => handleBackup(t)} title="Download SQL + branding files">
                              <i className="ti ti-download" /> Backup
                            </button>
                            <button className="btn btn-outline btn-sm" disabled={busyId === t.id} onClick={() => act(t.id, () => (Number(t.is_active) ? suspendTenant(t.id) : activateTenant(t.id)), Number(t.is_active) ? 'Tenant suspended' : 'Tenant activated')} style={{ color: Number(t.is_active) ? 'var(--amber)' : 'var(--green)' }}>
                              {Number(t.is_active) ? 'Suspend' : 'Unsuspend'}
                            </button>
                            <button className="btn btn-outline btn-sm" disabled={busyId === t.id} onClick={() => act(t.id, () => deleteTenant(t.id), 'Tenant deleted — data kept until you purge')} style={{ color: 'var(--red)' }}>
                              Delete
                            </button>
                          </>
                        )}
                        {t.status === 'deleted' && (
                          <>
                            <button className="btn btn-outline btn-sm" disabled={busyId === t.id} onClick={() => handleBackup(t)} title="Download a backup before purging">
                              <i className="ti ti-download" /> Backup
                            </button>
                            <button className="btn btn-outline btn-sm" disabled={busyId === t.id} onClick={() => act(t.id, () => restoreTenant(t.id), 'Tenant restored')} style={{ color: 'var(--green)' }}>
                              Restore
                            </button>
                            <button className="btn btn-danger btn-sm" disabled={busyId === t.id} onClick={() => { setPurgeTarget(t); setPurgeConfirm(''); }}>
                              Purge
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === t.id && (
                    <TenantDetail tenant={t} toast={toast} onImpersonateUrl={setImpersonateUrl} />
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Tenant" width={460}>
        <div className="form-group">
          <label className="form-label">Company Name *</label>
          <input className="form-input" value={newTenant.name} onChange={e => setNewTenant(t => ({ ...t, name: e.target.value }))} placeholder="Acme Inc" autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Subdomain *</label>
          <input className="form-input" value={newTenant.subdomain} onChange={e => setNewTenant(t => ({ ...t, subdomain: e.target.value.toLowerCase() }))} placeholder="acme" />
        </div>
        <div className="form-group">
          <label className="form-label">Admin Email</label>
          <input className="form-input" type="email" value={newTenant.admin_email} onChange={e => setNewTenant(t => ({ ...t, admin_email: e.target.value }))} placeholder="admin@acme.com" />
          <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>The tenant's first admin login. The setup link is shown to you after provisioning to hand over.</p>
        </div>
        <div className="form-actions">
          <button className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? 'Adding...' : 'Add Tenant'}</button>
        </div>
      </Modal>

      <SetupLinkModal
        url={setupUrl}
        title="Tenant setup link"
        note="Share this one-time link with the tenant's admin so they can set their password. It expires in 7 days."
        onClose={() => setSetupUrl('')}
      />
      <SetupLinkModal
        url={impersonateUrl}
        title="Impersonation link"
        note="Open this to sign in as that user for 1 hour. It is single-use, expires in 2 minutes, and the session is logged."
        onClose={() => setImpersonateUrl('')}
      />

      {/* Purge — the only irreversible action, so it's gated behind typing the subdomain. */}
      <Modal open={!!purgeTarget} onClose={() => setPurgeTarget(null)} title="Purge tenant permanently" width={470}>
        {purgeTarget && (
          <>
            <div style={{ background: 'var(--red-light)', border: '0.5px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#991b1b', marginBottom: 4 }}>This cannot be undone.</div>
              <div style={{ fontSize: 12, color: 'var(--gray-600)' }}>
                Drops the database <strong>{purgeTarget.db_name}</strong>, removes its domain(s) from Webuzo, and deletes the tenant record.
                Download a backup first if you might need this data.
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Type <strong>{purgeTarget.subdomain}</strong> to confirm</label>
              <input className="form-input" value={purgeConfirm} onChange={e => setPurgeConfirm(e.target.value)} placeholder={purgeTarget.subdomain} autoFocus />
            </div>
            <div className="form-actions">
              <button className="btn btn-outline" onClick={() => setPurgeTarget(null)}>Cancel</button>
              <button
                className="btn btn-danger"
                disabled={purgeConfirm !== purgeTarget.subdomain || busyId === purgeTarget.id}
                onClick={handlePurge}
              >
                {busyId === purgeTarget.id ? 'Purging…' : 'Purge permanently'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
